import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ArtworkDataContext from '@/context/ArtworkDataContext';
import useGetSearchParams from '@/hooks/useGetSearchParams';
import useProtectActionUnregistered from '@/hooks/useProtectActionUnregistered';
import useStateHandler from '@/hooks/useStateHandler';
import Loader from '@/shared-components/loader/Loader';
import UnregisteredPopup from '@/shared-components/popups/unregistered-user-popup';
import AuctionUnregisteredPopup from '@/shared-components/popups/unregistered-user-popup/auction-unregistered-popup';

import ContentSection from '../content-section';
import DesktopControls from '../content-section/components/DesktopControls';
import useArtworkSlider from '../hooks/useArtworkSlider';
import useArtworkViewContext from '../hooks/useArtworkViewContext';
import ArtworkMobileView from '../mobile-view';
import PicturesSection from '../pictures-section';
import LimitedEditionRoomViewAction from '../room-view/LimitedEditionRoomViewAction';

import ArtworkMetaDesc from './ArtworkMetaDesc';

export default function ArtworkSlide({
  artworkData,
  isMobileLayout,
  isActive,
  hasPrev,
  hasNext,
  displayLoader,
  detailsOpen,
  openLimitedEditionCollectors,
  onUnlockSoldPrice,
  soldPricePending,
  useAuctionVariant = false,
  isStandaloneVisit = false,
}) {
  const location = useLocation();
  const roomViewParams = new URLSearchParams(location.search);
  const roomViewSelection = roomViewParams.get('room_view') === 'limited_edition'
    && location.pathname.endsWith(`/artwork/${encodeURIComponent(artworkData?.slug ?? '')}`)
    ? {
      size: roomViewParams.get('room_size'),
      frame: roomViewParams.get('room_frame'),
      label: roomViewParams.get('room_label'),
    }
    : null;
  const { state } = useStateHandler();
  const viewerSessionId = state?.user_session?._id ?? state?.user_session?.id ?? null;
  const shouldLoadViewerContext = Boolean(viewerSessionId) && isActive;
  const shouldLoadPurchaseContext = !isMobileLayout || detailsOpen || useAuctionVariant;
  const {
    socialsData,
    setSocialsData,
    viewerContext,
    purchaseContext,
  } = useArtworkViewContext({
    artworkSlug: artworkData.slug,
    isActive,
    viewerSessionId,
    soldPriceAccessStatus: artworkData?.sold_price_access?.status,
    socialSummary: artworkData.social_summary,
    shouldLoadViewerContext,
    shouldLoadPurchaseContext: isActive && shouldLoadPurchaseContext,
  });
  const ownershipContext = artworkData.ownership_context ?? null;
  const scrollableDiv = useRef();
  const { onTouchStart, onTouchEnd, onWheel } = useArtworkSlider(scrollableDiv);
  const { showPopup } = useProtectActionUnregistered({
    typeOfProtection: "USER_LIMIT_VIEW",
    popupDelayMs: useAuctionVariant ? 0 : undefined,
  });
  const [params] = useGetSearchParams({ validParams: ["rate", "thought"] });
  const [selectedSocial, setSelectedSocial] = useState(() => {
    if(params?.rate) return "ratings";
    if(params?.thought) return "thoughts";
    return null;
  });
  const artworkId = artworkData?._id ?? null;
  const [limitedEditionPreviewState, setLimitedEditionPreviewState] = useState(() => ({
    isVisible: false,
    selectedSizeKey: null,
    selectedSizeData: null,
    selectedFrameKey: null,
    selectedMuseumLabelKey: null,
    artworkId,
  }));
  const [aiMessages, setAiMessages] = useState([]);

  // Reset the preview helper whenever the active artwork changes so the slider returns to the default view.
  useEffect(() => {
    setLimitedEditionPreviewState({
      isVisible: false,
      selectedSizeKey: null,
      selectedSizeData: null,
      selectedFrameKey: null,
      selectedMuseumLabelKey: null,
      artworkId,
    });
    setAiMessages([]);
  }, [artworkId]);

  const mergedArtworkData = useMemo(() => ({
    ...artworkData,
    ...(ownershipContext?.ownership_summary
      ? { ownership_summary: ownershipContext.ownership_summary }
      : {}),
    ...(viewerContext ? { viewer_context: viewerContext } : {}),
    ...(purchaseContext ? { purchase_context: purchaseContext } : {}),
    ...(ownershipContext ? { ownership_context: ownershipContext } : {}),
  }), [
    artworkData,
    ownershipContext?.ownership_summary,
    ownershipContext,
    purchaseContext,
    viewerContext,
  ]);

  return (
    <ArtworkDataContext.Provider
      value={{
        artworkData: mergedArtworkData,
        artworkViewerContext: viewerContext,
        artworkPurchaseContext: purchaseContext,
        artworkOwnershipContext: ownershipContext,
        socialsData,
        setSocialsData,
        aiMessages,
        setAiMessages,
        limitedEditionPreviewState,
        setLimitedEditionPreviewState,
        openLimitedEditionCollectors,
        onUnlockSoldPrice,
        soldPricePending,
      }}
    >
      {showPopup && (
        useAuctionVariant ? (
          <AuctionUnregisteredPopup showPopUp={showPopup} />
        ) : (
          <UnregisteredPopup showPopup={showPopup} />
        )
      )}
      <ArtworkMetaDesc artworkData={artworkData} />
      {isActive && roomViewSelection && (
        <LimitedEditionRoomViewAction
          artworkData={mergedArtworkData}
          selection={roomViewSelection}
          autoOpen
          showTrigger={false}
        />
      )}

      <div
        className={`artwork__container remove-scrollbar${isStandaloneVisit ? ' artwork__container--standalone' : ''}`}
        onTouchStart={isMobileLayout ? undefined : onTouchStart}
        onTouchEnd={isMobileLayout ? undefined : onTouchEnd}
        onWheel={isMobileLayout ? undefined : onWheel}
        ref={scrollableDiv}
      >
        <div className="artwork__wrap">
          {isMobileLayout ? (
            <ArtworkMobileView
              selectedSocial={selectedSocial}
              setSelectedSocial={setSelectedSocial}
              isStandaloneVisit={isStandaloneVisit}
            />
          ) : (
            <>
              <PicturesSection/>
              <DesktopControls hasPrev={hasPrev} hasNext={hasNext}/>
              <ContentSection
                selectedSocial={selectedSocial}
                setSelectedSocial={setSelectedSocial}
              />
            </>
          )}
        </div>

        {displayLoader && (
          <Loader className="artwork__spinner" thin />
        )}
      </div>
    </ArtworkDataContext.Provider>
  );
}
